/**
 * Authentication Context
 * 
 * Role Resolution Strategy (without custom claims):
 * - User role is stored in Firestore users collection
 * - On auth state change, we fetch the user document
 * - Role is cached in React state until logout
 * 
 * Tradeoffs:
 * + Simpler than custom claims (no token refresh dance)
 * + Role changes are immediate (no waiting for token)
 * - One extra Firestore read on login
 * - If role changes server-side, user must re-login to see it
 * 
 * For MVP, this is the right tradeoff.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import {
  User as FirebaseUser,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  setPersistence,
  browserSessionPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, firebaseConfigError } from "../lib/firebase";
import { User, UserRole, SCHOOL_ID } from "../types";

// =============================================================================
// TYPES
// =============================================================================

interface AuthState {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  /** True after first onAuthStateChanged has run (auth fully initialized for callables). */
  authReady: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  /** rememberMe=true → local persistence (survives tab close). Default false = session only. */
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  isAdmin: boolean;
}

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

function ConfigErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="max-w-lg rounded-lg border border-amber-200 bg-amber-50 p-6 text-amber-900 shadow-sm">
        <h1 className="text-lg font-semibold">Configuration required</h1>
        <p className="mt-2 text-sm whitespace-pre-wrap">{message}</p>
        <p className="mt-3 text-sm">
          Then restart the dev server or rebuild for production.
        </p>
      </div>
    </div>
  );
}

export function AuthProvider({ children }: AuthProviderProps) {
  if (firebaseConfigError || !auth || !db) {
    return (
      <ConfigErrorScreen
        message={
          firebaseConfigError ||
          "Firebase is not configured. Add VITE_FIREBASE_* to frontend/.env."
        }
      />
    );
  }

  const [state, setState] = useState<AuthState>({
    user: null,
    firebaseUser: null,
    loading: true,
    authReady: false,
    error: null,
  });

  // ---------------------------------------------------------------------------
  // Fetch user data from Firestore
  // ---------------------------------------------------------------------------
  const fetchUserData = useCallback(async (firebaseUser: FirebaseUser): Promise<User | null> => {
    try {
      const userDocRef = doc(db!, "users", firebaseUser.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        console.error("User document not found in Firestore");
        return null;
      }

      const userData = userDocSnap.data();

      // Verify user belongs to our school (MVP: single school)
      if (userData.schoolId !== SCHOOL_ID) {
        console.error("User does not belong to this school");
        return null;
      }

      const user: User = {
        id: userData.id,
        email: userData.email,
        role: userData.role as UserRole,
        schoolId: userData.schoolId,
        createdAt: userData.createdAt?.toDate() || new Date(),
      };
      if (typeof userData.name === "string" && userData.name.trim()) {
        user.name = userData.name.trim();
      }
      if (userData.role === "student" && userData.childId) {
        user.childId = userData.childId;
      }
      return user;
    } catch (error) {
      console.error("Error fetching user data:", error);
      return null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Listen to auth state changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth!, async (firebaseUser) => {
      // Auth has finished initializing (persistence restored or signed out)
      if (firebaseUser) {
        // User is signed in - fetch their data from Firestore
        const user = await fetchUserData(firebaseUser);

        if (user) {
          setState({
            user,
            firebaseUser,
            loading: false,
            authReady: true,
            error: null,
          });
        } else {
          // User exists in Auth but not in Firestore (or wrong school)
          // Sign them out
          await firebaseSignOut(auth!);
          setState({
            user: null,
            firebaseUser: null,
            loading: false,
            authReady: true,
            error: "Account not found. Please contact administrator.",
          });
        }
      } else {
        // User is signed out
        setState({
          user: null,
          firebaseUser: null,
          loading: false,
          authReady: true,
          error: null,
        });
      }
    });

    return () => unsubscribe();
  }, [fetchUserData]);

  // ---------------------------------------------------------------------------
  // Sign in
  // ---------------------------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string, rememberMe = false): Promise<void> => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      // Admins use session persistence (tab close = logout) for stricter security.
      // Parents can opt into local persistence so they stay logged in across visits.
      await setPersistence(auth!, rememberMe ? browserLocalPersistence : browserSessionPersistence);
      await signInWithEmailAndPassword(auth!, email, password);
      // onAuthStateChanged will handle fetching user data
    } catch (error: any) {
      let errorMessage = "Failed to sign in";

      switch (error.code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
          errorMessage = "Invalid email or password";
          break;
        case "auth/too-many-requests":
          errorMessage = "Too many attempts. Please try again later.";
          break;
        case "auth/user-disabled":
          errorMessage = "This account has been disabled";
          break;
        case "auth/invalid-email":
          errorMessage = "Invalid email address";
          break;
      }

      setState((prev) => ({
        ...prev,
        loading: false,
        authReady: true,
        error: errorMessage,
      }));

      throw new Error(errorMessage);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Sign out
  // ---------------------------------------------------------------------------
  const signOut = useCallback(async (): Promise<void> => {
    try {
      await firebaseSignOut(auth!);
      // onAuthStateChanged will handle clearing state
    } catch (error) {
      console.error("Error signing out:", error);
      throw error;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Clear error
  // ---------------------------------------------------------------------------
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // ---------------------------------------------------------------------------
  // Context value
  // ---------------------------------------------------------------------------
  const value: AuthContextType = {
    ...state,
    signIn,
    signOut,
    clearError,
    isAdmin: state.user?.role === "admin",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}
